// Drei-Wege-Abgleich fuer Formularfelder (Spec §2, 2026-09-15).
//
// Wozu: Wenn beim Speichern ein 409 zurueckkommt, hat jemand anders seit dem
// Laden gespeichert. Statt „alles oder nichts" (bisheriger Dreiwege-Dialog)
// werden die Formulare feldgenau zusammengefuehrt: `base` = der Stand beim
// Laden, `mine` = mein Formular, `theirs` = der frische Serverstand. Nur Felder,
// die BEIDE Seiten unterschiedlich geaendert haben, landen als Konflikt im
// Dialog — alles andere fuehrt die Funktion stillschweigend zusammen.
//
// Bewusst reine Logik ohne React/API-Bezug: Admin-Detailseite, Split-View-Panel
// und Self-Service teilen sich dieselbe Funktion.

export interface MergeResult<T> {
  /** Zusammengefuehrtes Formular. Bei Konflikten steht darin MEIN Wert. */
  merged: T;
  /** Felder, die beide Seiten unterschiedlich geaendert haben. */
  conflicts: (keyof T)[];
  /** Felder, die nur die andere Seite geaendert hat (fuer den Toast). */
  theirsApplied: (keyof T)[];
}

// „Leer" ist in diesen Formularen mehrdeutig: das Formular schreibt `null`, ein
// gerade geleertes Eingabefeld liefert `''`, und ein Serverfeld kann `undefined`
// sein. Fuer den Abgleich sind alle drei derselbe Zustand — sonst meldete jedes
// einmal angefasste Leerfeld eine Aenderung.
function isEmptyish(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/**
 * Feldvergleich des Abgleichs (`eq` der Spec). Exportiert, damit Aufrufer
 * dieselbe Gleichheit verwenden koennen (z. B. „hat sich ueberhaupt etwas
 * geaendert?"), statt eine zweite, leicht abweichende Variante zu bauen.
 *
 * - Arrays nach Inhalt, Reihenfolge egal (Sprachen/Tags kommen sortiert oder
 *   nicht, je nach Eingabeweg)
 * - Strings getrimmt
 * - `null` / `undefined` / `''` / nur Leerraum gleichwertig
 * - Booleans und Zahlen per `===` (`false` und `0` sind NICHT leer)
 * - alles Uebrige per `JSON.stringify` als Rueckfall
 */
export function fieldEquals(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const sa = [...a].map(String).sort();
    const sb = [...b].map(String).sort();
    return sa.every((v, i) => v === sb[i]);
  }
  const ea = isEmptyish(a);
  const eb = isEmptyish(b);
  if (ea || eb) return ea && eb;
  if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
  if (typeof a === 'object' && typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
}

/**
 * Fuehrt `mine` und `theirs` gegen den gemeinsamen Ausgangsstand `base`
 * zusammen. Iteriert ueber die Schluessel von `base` — Schluessel, die nur eine
 * der beiden Seiten kennt, bleiben unberuehrt. Die Eingaben werden nicht
 * veraendert; `merged` traegt jeweils den ORIGINALWERT der gewinnenden Seite
 * (ungetrimmt), damit der Nutzer seine Eingabe unveraendert wiederfindet.
 */
export function threeWayMerge<T extends object>(base: T, mine: T, theirs: T): MergeResult<T> {
  const merged = { ...base };
  const conflicts: (keyof T)[] = [];
  const theirsApplied: (keyof T)[] = [];

  for (const key of Object.keys(base) as (keyof T)[]) {
    const b = base[key];
    const m = mine[key];
    const t = theirs[key];
    const mineChanged = !fieldEquals(m, b);
    const theirsChanged = !fieldEquals(t, b);

    if (!mineChanged && !theirsChanged) {
      merged[key] = b;
    } else if (mineChanged && !theirsChanged) {
      merged[key] = m;
    } else if (!mineChanged && theirsChanged) {
      merged[key] = t;
      theirsApplied.push(key);
    } else if (fieldEquals(m, t)) {
      // Beide haben dasselbe gemeint — kein Konflikt.
      merged[key] = m;
    } else {
      merged[key] = m;
      conflicts.push(key);
    }
  }

  return { merged, conflicts, theirsApplied };
}
